import { Testnet, Mainnet } from "@nibiruchain/nibijs";

/**
 * Determine if we're on testnet based on environment variable
 * @returns {boolean} True if testnet, false if mainnet
 */
export const isTestnet = () => {
  // PRIORITY 1: If mainnet chain ID is explicitly set, we're on mainnet
  const mainnetChainId = process.env.REACT_APP_CHAIN_ID;
  if (mainnetChainId && mainnetChainId.trim() !== '') {
    console.log('Mainnet detected via REACT_APP_CHAIN_ID:', mainnetChainId);
    return false;
  }
  
  // PRIORITY 2: Explicit network flag
  const networkFlag = process.env.REACT_APP_NETWORK;
  if (networkFlag === 'testnet') {
    console.log('Testnet detected via REACT_APP_NETWORK flag');
    return true;
  }
  if (networkFlag === 'mainnet') {
    console.log('Mainnet detected via REACT_APP_NETWORK flag');
    return false;
  }
  
  // PRIORITY 3: If testnet chain ID is set (and no mainnet chain ID), we're on testnet
  const testnetChainId = process.env.REACT_APP_TESTNET_CHAIN_ID;
  if (testnetChainId && testnetChainId.trim() !== '' && !mainnetChainId) {
    console.log('Testnet detected via REACT_APP_TESTNET_CHAIN_ID:', testnetChainId);
    return true;
  }
  
  // Default to mainnet (safest default)
  console.log('Defaulting to mainnet (no explicit network config found)');
  return false;
};

/**
 * Get RPC endpoint for current network
 * @returns {string} RPC endpoint URL
 */
export const getRpcEndpoint = () => {
  if (isTestnet()) {
    return process.env.REACT_APP_TESTNET_RPC || 'https://rpc.testnet-2.nibiru.fi:443';
  }
  return process.env.REACT_APP_MAINNET_RPC || 'https://rpc.nibiru.fi:443';
};

/**
 * Get REST endpoint for current network
 * @returns {string} REST endpoint URL
 */
export const getRestEndpoint = () => {
  if (isTestnet()) {
    return process.env.REACT_APP_TESTNET_REST || 'https://lcd.testnet-2.nibiru.fi';
  }
  return process.env.REACT_APP_MAINNET_REST || 'https://lcd.nibiru.fi';
};

/**
 * Get the appropriate Nibiru SDK instance
 * @returns {Object} Nibiru SDK instance (Mainnet or Testnet)
 */
export const getNibiruSDK = () => {
  const isTestnetMode = isTestnet();
  
  // Debug logging with all env vars
  const envVars = {
    isTestnet: isTestnetMode,
    REACT_APP_CHAIN_ID: process.env.REACT_APP_CHAIN_ID,
    REACT_APP_TESTNET_CHAIN_ID: process.env.REACT_APP_TESTNET_CHAIN_ID,
    REACT_APP_NETWORK: process.env.REACT_APP_NETWORK,
    expectedChainId: getChainId()
  };
  console.log('🔍 Network detection:', envVars);
  
  if (isTestnetMode) {
    // Use testnet-2 explicitly
    const testnet = Testnet(2);
    console.log('✅ Using Testnet SDK, RPC:', testnet.endptTm, 'Chain ID:', testnet.chainId);
    return testnet;
  }
  
  // Use mainnet - Mainnet() should default to https://rpc.nibiru.fi:443
  const mainnet = Mainnet();
  
  // Verify the SDK is using mainnet endpoints
  const expectedRpc = getRpcEndpoint();
  const expectedChainId = getChainId();
  
  if (mainnet.endptTm && mainnet.endptTm.includes('testnet')) {
    console.error('ERROR: Mainnet SDK is using testnet RPC!', mainnet.endptTm);
    console.error('Expected mainnet RPC:', expectedRpc);
    throw new Error('Mainnet SDK is configured with testnet RPC endpoint!');
  }
  
  if (mainnet.chainId && mainnet.chainId.includes('testnet')) {
    console.error('ERROR: Mainnet SDK is using testnet chain ID!', mainnet.chainId);
    console.error('Expected mainnet chain ID:', expectedChainId);
    throw new Error('Mainnet SDK is configured with testnet chain ID!');
  }
  
  console.log('✅ Using Mainnet SDK, RPC:', mainnet.endptTm || expectedRpc, 'Chain ID:', mainnet.chainId || expectedChainId);
  
  return mainnet;
};

/**
 * Get current network name
 * @returns {string} 'testnet' or 'mainnet'
 */
export const getNetworkName = () => {
  return isTestnet() ? 'testnet' : 'mainnet';
};

/**
 * Get chain ID for current network
 * @returns {string} Chain ID
 */
export const getChainId = () => {
  if (isTestnet()) {
    return process.env.REACT_APP_TESTNET_CHAIN_ID || 'nibiru-testnet-2';
  }
  return process.env.REACT_APP_CHAIN_ID || 'cataclysm-1';
};

/**
 * Get contract addresses for current network
 * @returns {Object} Contract addresses object with oracle, perp, vaultMinter, vault
 */
export const getContractAddresses = () => {
  if (isTestnet()) {
    return {
      oracle: process.env.REACT_APP_TESTNET_ORACLE_SMART_CONTRACT || '',
      perp: process.env.REACT_APP_TESTNET_PERP_SMART_CONTRACT || '',
      vaultMinter: process.env.REACT_APP_TESTNET_VAULT_TOKEN_MINTER_SMART_CONTRACT || '',
      vault: process.env.REACT_APP_TESTNET_VAULT_SMART_CONTRACT || '',
    };
  }
  return {
    oracle: process.env.REACT_APP_ORACLE_SMART_CONTRACT || '',
    perp: process.env.REACT_APP_PERP_SMART_CONTRACT || '',
    vaultMinter: process.env.REACT_APP_VAULT_TOKEN_MINTER_SMART_CONTRACT || '',
    vault: process.env.REACT_APP_VAULT_SMART_CONTRACT || '',
  };
};

/**
 * Get collateral denom for current network
 * @returns {string} Collateral denomination string
 */
export const getCollateralDenom = () => {
  if (isTestnet()) {
    return process.env.REACT_APP_TESTNET_COLLATERAL_DENOM || '';
  }
  return process.env.REACT_APP_COLLATERAL_DENOM || '';
};

/**
 * Get Cosmos transaction explorer URL for current network
 * @param {string} txHash - Transaction hash
 * @returns {string} Full explorer URL
 */
export const getCosmosExplorerUrl = (txHash) => {
  if (isTestnet()) {
    return `https://explorer.nibiru.fi/nibiru-testnet-2/tx/${txHash}`;
  }
  return `https://explorer.nibiru.fi/cataclysm-1/tx/${txHash}`;
};

/**
 * Get EVM transaction explorer URL for current network
 * @param {string} txHash - Transaction hash
 * @returns {string} Full explorer URL
 */
export const getEvmExplorerUrl = (txHash) => {
  if (isTestnet()) {
    return `https://testnet.nibiscan.io/tx/${txHash}`;
  }
  return `https://nibiscan.io/tx/${txHash}`;
};

/**
 * Get Keplr-compatible chain configuration for current network.
 * Used with window.keplr.experimentalSuggestChain() when the chain
 * isn't already registered in the user's Keplr wallet.
 * @returns {Object} Keplr chain config
 */
export const getKeplrChainConfig = () => {
  const chainId = getChainId();
  const rpc = getRpcEndpoint();
  const rest = getRestEndpoint();

  const shared = {
    bip44: { coinType: 118 },
    bech32Config: {
      bech32PrefixAccAddr: "nibi",
      bech32PrefixAccPub: "nibipub",
      bech32PrefixValAddr: "nibivaloper",
      bech32PrefixValPub: "nibivaloperpub",
      bech32PrefixConsAddr: "nibivalcons",
      bech32PrefixConsPub: "nibivalconspub",
    },
    currencies: [{
      coinDenom: "NIBI",
      coinMinimalDenom: "unibi",
      coinDecimals: 6,
      coinGeckoId: "nibiru",
    }],
    feeCurrencies: [{
      coinDenom: "NIBI",
      coinMinimalDenom: "unibi",
      coinDecimals: 6,
      coinGeckoId: "nibiru",
    }],
    stakeCurrency: {
      coinDenom: "NIBI",
      coinMinimalDenom: "unibi",
      coinDecimals: 6,
      coinGeckoId: "nibiru",
    },
    gasPriceStep: { low: 0.01, average: 0.025, high: 0.04 },
    features: ["stargate", "ibc-transfer"],
  };

  return {
    chainId,
    chainName: isTestnet() ? "Nibiru Testnet 2" : "Nibiru Mainnet",
    rpc,
    rest,
    ...shared,
  };
};

